import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck } from "lucide-react";

const TERMS_TEXT = `Al usar NomiDiem confirmas que autorizas los descuentos correspondientes a los adelantos solicitados en el siguiente ciclo de nómina. Declinas cualquier reclamación por los adelantos autorizados.`;

export default function TermsGate({ children }) {
  const { user, setUser } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const needsTerms =
    user && user.role === "company" && !user.terms_accepted_at;

  if (!needsTerms) return children;

  const accept = async () => {
    if (!accepted) return toast.error("Debes marcar la casilla para continuar");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/accept-terms");
      setUser({ ...user, terms_accepted_at: data.terms_accepted_at });
      toast.success("Términos aceptados");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="terms-gate">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-2xl overflow-hidden my-8">
        <div className="bg-[#1D9E75] p-6 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-md flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Términos y condiciones</h1>
              <p className="text-xs opacity-90 mt-0.5">Requerido antes de continuar</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-[#64748B]">
            Estimado(a) <strong className="text-[#0F172A]">{user.name}</strong>, para poder usar la plataforma NomiDiem debes aceptar los siguientes términos:
          </p>

          <div className="bg-[#F8FAFC] border-l-4 border-[#1D9E75] rounded-md p-4">
            <p className="text-sm text-[#0F172A] leading-relaxed" data-testid="terms-text">
              {TERMS_TEXT}
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="terms-checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#1D9E75] shrink-0"
            />
            <span className="text-sm text-[#334155]">
              He leído y acepto los términos descritos en nombre de la empresa que represento.
            </span>
          </label>

          <div className="pt-2 border-t border-[#E2E8F0] flex items-center justify-between gap-3">
            <div className="text-[11px] text-[#94A3B8]">
              Tu aceptación quedará registrada con fecha y hora.
            </div>
            <Button
              onClick={accept}
              disabled={!accepted || submitting}
              data-testid="terms-accept-btn"
              className="bg-[#1D9E75] hover:bg-[#147D5C] text-white"
            >
              {submitting ? "Aceptando..." : "Aceptar y continuar"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
